import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageSEO, PageFAQSchema } from '../../components/seo/SchemaMarkup';
import {
  API, EmailCapture, Eyebrow, FacilityCard, FaqBlock, InstitutionalBlock,
  JumpChips, LocatorLoading, MetroGrid, StatBar, lcTherapy, localBusinessList, useJsonLd, useLocatorPageView,
} from './locatorShared';

const HYROX_GUIDES = {
  'dallas-fort-worth': { label: 'Dallas–Fort Worth', article: 'dallas-fort-worth', blurb: '31 gyms · official HYROX Training Center · drop-ins from $15' },
  austin: { label: 'Austin', article: 'austin', blurb: '12 gyms · 8 official Training Clubs · day passes from $19' },
  denver: { label: 'Denver', article: 'denver', blurb: '16 gyms · Arsenal Health featured · 24/7 fob access' },
  tampa: { label: 'Tampa', article: 'tampa', blurb: '15 gyms · F45 Largo East featured · full HYROX sims' },
  'salt-lake-city': { label: 'Salt Lake City', article: 'salt-lake-city', blurb: '15 gyms · F45 Sugarhouse featured · 5-for-$35 intro' },
  'las-vegas': { label: 'Las Vegas', article: 'las-vegas', blurb: '13 gyms · 9 official Training Clubs' },
  'orange-county': { label: 'Anaheim & Orange County', article: 'anaheim', blurb: '10 gyms · GRND Fitness + OC Performance Center' },
  'san-diego': { label: 'San Diego', article: 'san-diego', blurb: '13 gyms · MROC Training featured · 3 classes for $30' },
  nashville: { label: 'Nashville', article: 'nashville', blurb: '16 gyms · Blueprint Fitness featured · free pass' },
  chicago: { label: 'Chicago', article: 'chicago', blurb: '8 gyms · 6 official clubs · free intro at South Loop S&C' },
  miami: { label: 'Miami', article: 'miami', blurb: '12 gyms · HYROX-named studios · Wynwood to the Beach' },
  'washington-dc': { label: 'Washington DC', article: 'washington-dc', blurb: '10 gyms · DMV coverage · DC, Arlington & Bethesda' },
  phoenix: { label: 'Phoenix', article: 'phoenix', blurb: '9 gyms · Scottsdale to Tempe · HYROX programs' },
  'new-york': { label: 'New York City', article: 'new-york-city', blurb: '4 gyms · Manhattan + Brooklyn · elite race prep' },
  'los-angeles': { label: 'Los Angeles', article: 'los-angeles', blurb: '7 gyms · 5 official Training Clubs · DTLA to Beverly Hills' },
  atlanta: { label: 'Atlanta', article: 'atlanta', blurb: '7 gyms · 4 official Training Clubs · Westside & BeltLine' },
  minneapolis: { label: 'Minneapolis', article: 'minneapolis', blurb: '5 gyms · Tonka Fitness official affiliate · Twin Cities' },
  'bay-area': { label: 'San Francisco & the Bay Area', article: 'san-francisco', blurb: '7 gyms · 2 official Training Clubs · Bay-wide coverage' },
};

const EXTRA_GUIDES = {
  'dallas-fort-worth': [
    {
      href: '/blogs/article/the-best-running-clubs-across-the-usa-where-community-meets-the-pavement#runitupdallas',
      title: 'The Best Running Clubs Across the USA',
      sub: '34 clubs independently scored — 4 in Dallas. Run It Up Dallas rated 84/100: free, beginner-friendly, strong no-drop policy.',
    },
  ],
};

const relatedGuidesFor = (slug) => [
  ...(EXTRA_GUIDES[slug] || []),
  ...(HYROX_GUIDES[slug] ? [{
    href: `/blogs/article/the-best-hyrox-training-gyms-in-${HYROX_GUIDES[slug].article}-your-complete-directory`,
    title: `The Best HYROX Training Gyms in ${HYROX_GUIDES[slug].label}`,
    sub: `${HYROX_GUIDES[slug].blurb}. Full directory with coaches, class times and direct booking links.`,
  }] : []),
];

export default function LocatorDirectory() {
  const { slug } = useParams();
  useLocatorPageView(slug);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setNotFound(false);
    fetch(`${API}/api/locator/page/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  const allFacilities = data ? data.sections.flatMap((s) => s.facilities) : [];
  const unique = [...new Map(allFacilities.map((f) => [f.slug, f])).values()];
  useJsonLd(
    'locator-page-itemlist',
    data ? localBusinessList(unique, `/locator/${slug}`, `Best Recovery & Peptide Therapy in ${data.name}`) : null,
  );

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>No Directory Here Yet</h1>
          <Link to="/locator" className="text-[#C7FA3E] text-sm underline underline-offset-2">Browse all recovery directories</Link>
        </div>
      </div>
    );
  }
  if (!data) return <LocatorLoading />;

  const isMetro = data.kind === 'metro';
  const relatedGuides = isMetro ? relatedGuidesFor(slug) : [];
  const title = isMetro
    ? `Best Recovery & Peptide Therapy in ${data.name}${data.state ? ` (${data.state})` : ''} — ${data.stats.total} Vetted Clinics 2026`
    : `Best Recovery Facilities in ${data.name} — ${data.stats.total} Vetted Clinics 2026`;

  return (
    <article className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid={`locator-page-${slug}`}>
      <PageSEO
        title={`${title} | NinjAthlete`}
        description={`Find recovery therapy in ${data.name}. ${data.stats.total} vetted facilities with contact details and booking links. Compare ${data.stats.top.map((t) => t.therapy).join(', ')} clinics.`}
        path={`/locator/${slug}`}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Recovery Locator', url: '/locator' }, { name: data.name, url: `/locator/${slug}` }]}
      />
      <PageFAQSchema faqs={data.faqs.map((f) => ({ question: f.question, answer: f.answer }))} pageName={title} pagePath={`/locator/${slug}`} scriptId="locator-page-faq" />
      <div className="max-w-5xl mx-auto">
        <Eyebrow>Recovery &amp; Peptide Therapy · {data.name} · Updated August 2026</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="locator-page-h1">
          The Best Recovery &amp; Peptide Therapy {isMetro ? 'Clinics in' : 'Facilities in'} {data.name}
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-2xl">
          Every <strong className="text-white">{data.name} recovery facility</strong> below is vetted and verified — covering {data.stats.top.map((t) => lcTherapy(t.therapy)).join(', ')} and more. Facility owners can claim their listing free to add pricing, photos and a booking link.
        </p>

        <StatBar
          testPrefix={`locator-${slug}`}
          stats={[
            [`${data.stats.total}`, 'Facilities listed'],
            ...data.stats.top.slice(0, 3).map((t) => [`${t.count}`, `Offer ${lcTherapy(t.therapy)}`]),
          ]}
        />

        <JumpChips
          testPrefix={`locator-${slug}`}
          chips={[
            ...data.sections.map((s) => [`#section-${s.therapy.slug}`, s.therapy.name]),
            ['#locator-faq', 'FAQ'],
          ].slice(0, 12)}
        />

        <EmailCapture metro={slug} metroName={data.name} testPrefix={`locator-${slug}`} />

        {data.metros_in_state?.length > 0 && (
          <p className="text-zinc-400 text-sm mb-6">
            Looking for a specific city? See{' '}
            {data.metros_in_state.map((m, i) => (
              <span key={m.slug}>
                {i > 0 && ' · '}
                <Link to={`/locator/${m.slug}`} className="text-[#C7FA3E] hover:underline">{m.name}</Link>
              </span>
            ))}
          </p>
        )}

        {data.sections.map((s) => (
          <section key={s.therapy.slug} id={`section-${s.therapy.slug}`} className="mt-10 scroll-mt-28" data-testid={`section-${s.therapy.slug}`}>
            <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
              <h2 className="text-white text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                {s.therapy.name}{typeof s.therapy.count === 'number' ? ` (${s.therapy.count})` : ''}
              </h2>
              {s.sub_page && <Link to={s.sub_page} className="text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline">Dedicated {s.therapy.name} page →</Link>}
            </div>
            <p className="text-zinc-500 text-sm mb-4">
              {s.therapy.blurb}{s.therapy.pillar && <> <a href={s.therapy.pillar} className="text-[#C7FA3E] hover:underline">Read our {s.therapy.name.toLowerCase()} guide</a>.</>}
            </p>
            <div className="space-y-4">
              {s.facilities.map((f) => <FacilityCard key={f.slug} facility={f} page={slug} />)}
            </div>
          </section>
        ))}

        <InstitutionalBlock facilities={data.institutional} testPrefix={`locator-${slug}`} />

        {relatedGuides.length > 0 && (
          <section className="mt-12" data-testid={`locator-${slug}-related-guides`}>
            <h2 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Also on NinjAthlete</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedGuides.map((g) => (
                <Link key={g.href} to={g.href} className="block border border-zinc-800 bg-zinc-900/50 p-5 hover:border-[#C7FA3E]/50 transition-colors group" data-testid={`related-guide-${g.href.split('/').pop().split('#')[0].slice(0, 30)}`}>
                  <h3 className="text-white text-sm font-bold group-hover:text-[#C7FA3E] transition-colors">{g.title}</h3>
                  <p className="text-zinc-500 text-xs mt-1.5 leading-relaxed">{g.sub}</p>
                  <span className="inline-block text-[#C7FA3E] text-[11px] uppercase tracking-wider mt-3">Read the guide →</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <FaqBlock faqs={data.faqs} testPrefix={`locator-${slug}`} />

        <MetroGrid metros={data.other_metros} testPrefix={`locator-${slug}`} />

        <p className="text-zinc-500 text-sm mt-10">
          Go deeper: our <Link to="/peptides" className="text-[#C7FA3E] hover:underline">peptide therapy guides</Link>,{' '}
          <Link to="/recovery" className="text-[#C7FA3E] hover:underline">recovery hub</Link> and{' '}
          <Link to="/hydrotherapy" className="text-[#C7FA3E] hover:underline">hydrotherapy science</Link> cover what to ask before you book.
        </p>
      </div>
    </article>
  );
}
